import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronUpLine,
  MoreHorizontalLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import type { Connectors } from "@mirage/service-connectors/service";
import {
  connectorToSearchFilter,
  filterOutLegacyPaperConnector,
  type ConnectorFilter,
  type SearchFilter,
} from "@mirage/shared/search/search-filters";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppFilterRow } from "./AppFilterRow";
import { FilterRow } from "./FilterRow";
import styles from "./FilterRow.module.css";

const DEFAULT_NUM_APP_FILTERS_SHOWN = 5;

type AppFilterGroupProps = {
  query: string;
  connectors: Connectors;
  seeAllForFilter: (filter: SearchFilter) => void;
  shouldSkipCache?: boolean;
};

export const AppFilterGroup = ({
  query,
  connectors,
  seeAllForFilter,
  shouldSkipCache,
}: AppFilterGroupProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(false);

  const appFilters: ConnectorFilter[] = useMemo(() => {
    return connectors
      .filter(filterOutLegacyPaperConnector)
      .map((connector) => connectorToSearchFilter(connector))
      .filter((filter): filter is ConnectorFilter => filter !== undefined);
  }, [connectors]);

  const moreOrLessRow = useMemo(() => {
    if (appFilters.length <= DEFAULT_NUM_APP_FILTERS_SHOWN) return null;
    const iconSrc = expanded ? ChevronUpLine : MoreHorizontalLine;
    return {
      title: expanded ? t("less") : t("more"),
      icon: <UIIcon className={styles.filterIcon} src={iconSrc} />,
      onClick: () => setExpanded(!expanded),
    };
  }, [expanded, appFilters.length]);
  const displayedFilters = useMemo(() => {
    if (expanded) return appFilters;
    return appFilters.slice(0, DEFAULT_NUM_APP_FILTERS_SHOWN);
  }, [expanded, appFilters.length]);
  return (
    <Box className={styles.filterGroup}>
      {displayedFilters.map((filter) => {
        return (
          <AppFilterRow
            key={filter.id}
            query={query}
            filter={filter}
            seeAllForFilter={seeAllForFilter}
            shouldSkipCache={shouldSkipCache}
          />
        );
      })}
      {moreOrLessRow && <FilterRow {...moreOrLessRow} />}
    </Box>
  );
};
